import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { observable } from 'mobx';

import { getLanguageFromLocale, isCMS } from '@ratehub/base-ui';

import BlogSpotlightBusinessUnits from '../definitions/BlogSpotlightBusinessUnits';
import BusinessUnitsMessages from '../definitions/BusinessUnitsMessages';
import fetchPostsForBlogSpotlight from '../functions/fetchPostsForBlogSpotlight';


function useBlogSpotlight({ initialPosts, initialBusinessUnit, hidePostFilter, blogPostCount }) {
    const intl = useIntl();
    const language = getLanguageFromLocale(intl.locale);

    const [ self ] = useState(() => observable({
        isFetching: false,
        posts: initialPosts,
        businessUnit: initialBusinessUnit,

        get businessUnitOptions() {
            if (hidePostFilter) {
                return undefined;
            }

            return Object.values(BlogSpotlightBusinessUnits).map(businessUnit => ({
                label: intl.formatMessage(BusinessUnitsMessages[businessUnit]),
                value: businessUnit,
            }));
        },

        async setBusinessUnit(value) {
            // Storing here incase we need to revert on error
            const previousBU = self.businessUnit;
            self.isFetching = true;
            self.businessUnit = value;

            const fetchOptions = {
                businessUnit: value,
                language: language,
                perPageCount: blogPostCount,
            };

            try {
                self.posts = await fetchPostsForBlogSpotlight(fetchOptions);
            } catch {
                self.businessUnit = previousBU;
                // eslint-disable-next-line no-console
                console.error('[ERROR] failed fetching blog posts');
            }

            self.isFetching = false;
        },
    }));

    // Fetch initial posts (only in the CMS)
    // The web-component in the CMS doesn't have initialPosts passed to it
    // because it's not SSR. This does an initial fetch for posts just so
    // we can show something to editors on initial load.
    useEffect(() => {
        if (isCMS() && Array.isArray(self.posts) && !self.posts.length && !self.isFetching) {
            self.setBusinessUnit(initialBusinessUnit);
        }
    }, [ self, initialBusinessUnit ]);

    return self;
}

export default useBlogSpotlight;
